import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges
} from '@angular/core';
import { LoSelect, LoSelectOption } from 'lo-design-system';
import { PlanCardLadderStop } from './plan-ladder.model';

/**
 * THE ALLOWANCE DIAL ON A PLAN CARD — a `lo-select` whose options are the
 * tier's rungs, each one the allowance, its price and, where the rung carries a
 * volume saving, the saving: "400 credits — $164/mo · Save 10%".
 *
 * An option is one line of text in `lo-select`, so the saving pill and the
 * right-aligned price this control used to draw are folded into the label, and
 * the closed trigger reads the whole rung. A single rung (or none) is the
 * allowance printed as text by the card: there is nothing to move, so no
 * control is drawn.
 *
 * Selection is by index, kept as the option's value, so two rungs that read
 * alike never collide.
 */
@Component({
	standalone: true,
	selector: 'app-plan-ladder-select',
	template: `
		<lo-select
			size="sm"
			[label]="label"
			[options]="options"
			[value]="selected"
			[hint]="footerNote"
			(valueChange)="onChange($event)"
		></lo-select>
	`,
	imports: [LoSelect]
})
export class PlanLadderSelectComponent implements OnChanges {
	@Input() stops: PlanCardLadderStop[] = [];
	@Input() index = 0;
	/** The dial's visible label, drawn above the control. */
	@Input() label = '';
	/** The upsell under the rungs. Empty renders no line at all. */
	@Input() footerNote = '';

	@Output() indexChange = new EventEmitter<number>();

	/** The rungs as options, resolved in `ngOnChanges` — never in a template
	 *  getter, which change detection re-runs on every pass. */
	options: LoSelectOption[] = [];
	selected: string | null = null;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['stops']) {
			this.options = this.stops.map((stop, stopIndex) => ({
				value: String(stopIndex),
				label: stopOptionLabel(stop)
			}));
		}
		if (changes['stops'] || changes['index']) {
			this.selected = this.stops.length ? String(this.index) : null;
		}
	}

	onChange(value: string | string[] | null): void {
		if (typeof value !== 'string') {
			return;
		}
		const next = Number(value);
		if (next !== this.index) {
			this.indexChange.emit(next);
		}
	}
}

/** "400 credits — $164/mo · Save 10%": the three parts of a rung on one line. */
function stopOptionLabel(stop: PlanCardLadderStop): string {
	const price = stop.price ? ` — ${stop.price}` : '';
	const saving = stop.savingPercent ? ` · Save ${stop.savingPercent}%` : '';
	return `${stop.quantity}${price}${saving}`;
}
